// @ts-nocheck
import {memo, useCallback, useEffect, useId, useState} from "react";
import {useTranslation} from "react-i18next";
import useToggleGameFeedLike from "../../../../hooks/games/useToggleGameFeedLike.hook";
import {useAuthStore} from "../../../../store/useAuth.store";
import CommentsPanel from "../../GameFeed/components/CommentsPanel";
import * as styles from "../_providerProfileCls";

const PostCard = ({game, providerName, providerInitial, onRequestAuth}) => {
  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const isAuthenticated = authStatus === "authenticated";
  const iframeId = useId();

  const [gameUrl, setGameUrl] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [localLiked, setLocalLiked] = useState(game.is_liked ?? false);
  const [localLikeCount, setLocalLikeCount] = useState(game.like_count ?? 0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const {toggleLike, isToggling} = useToggleGameFeedLike();

  useEffect(() => {
    setLocalLiked(game.is_liked ?? false);
    setLocalLikeCount(game.like_count ?? 0);
  }, [game.is_liked, game.like_count]);

  useEffect(() => {
    const bottomNav = document.querySelector(".bottomNavMenu");
    if (!bottomNav) return;
    bottomNav.style.visibility = isFullscreen ? "hidden" : "visible";
    return () => {
      bottomNav.style.visibility = "visible";
    };
  }, [isFullscreen]);

  const gameId = game.provider_game_id || game.id || game.game_id;
  const gameProvider =
    (typeof game.provider === "object" ? game.provider?.name : game.provider) || game.game_provider || providerName;
  const gameName = game.name || game.game_name;
  const icon = game.custom_icon || game.icon;

  const launchGame = useCallback(async () => {
    if (!isAuthenticated) {
      onRequestAuth?.();
      return;
    }
    if (isLaunching || gameUrl) return;

    setIsLaunching(true);
    try {
      const {ConfEnv} = await import("../../../../config/config");
      const useCurrencyStore = (await import("../../../../store/useCurrency.store")).default;
      const privateRequest = (await import("../../../../Infra/http/private.request")).default;

      const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
      const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";
      const isMobile = window.innerWidth <= 768;
      const url = `${ConfEnv.API_URL.GAMES_INIT}/${gameProvider}/${gameId}?currency=${currencyParam}&is_mobile=${isMobile}`;
      const response = await privateRequest.get(url);
      const launchUrl = response?.data?.data?.gameUrl ?? null;
      setGameUrl(launchUrl);
    } catch {
      /* silently handle launch errors */
    } finally {
      setIsLaunching(false);
    }
  }, [isAuthenticated, gameProvider, gameId, gameUrl, isLaunching, onRequestAuth]);

  const handleLike = () => {
    if (!isAuthenticated) {
      onRequestAuth?.();
      return;
    }
    setLocalLiked(prev => !prev);
    setLocalLikeCount(prev => (localLiked ? prev - 1 : prev + 1));
    toggleLike({gameId, gameProvider});
  };

  return (
    <div className={styles.postCardCls}>
      {/* Fullscreen game overlay */}
      {isFullscreen && gameUrl && (
        <div className={styles.gameFullscreenOverlayCls}>
          <button
            type="button"
            className={styles.gameFullscreenCloseCls}
            onClick={() => setIsFullscreen(false)}
            aria-label={t("gameFeedCloseGame")}
          >
            ✕
          </button>
          <iframe
            id={`${iframeId}-fs`}
            src={gameUrl}
            className={styles.gameFullscreenIframeCls}
            title={gameName}
            allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
          />
        </div>
      )}

      {/* Post header */}
      <div className={styles.postCardHeaderCls}>
        <div className={styles.postCardAvatarCls}>
          <div className={styles.postCardAvatarInnerCls}>{providerInitial}</div>
        </div>
        <div className={styles.postCardHeaderInfoCls}>
          <div className={styles.postCardProviderCls}>{providerName}</div>
          {gameName && <div className={styles.postCardGameNameCls}>{gameName}</div>}
        </div>
      </div>

      {/* Game image / play area */}
      <div className={styles.postCardImageContainerCls}>
        {gameUrl ? (
          <>
            <iframe
              id={iframeId}
              src={gameUrl}
              style={{width: "100%", height: "100%", border: "none", position: "absolute", inset: 0}}
              title={gameName}
              allow="clipboard-read; clipboard-write; screen-wake-lock; accelerometer; gyroscope"
            />
            <button
              type="button"
              className={styles.gameFullscreenCloseCls}
              style={{position: "absolute", bottom: 10, right: 10, top: "auto"}}
              onClick={() => setIsFullscreen(true)}
              aria-label={t("gameFeedExpandGame")}
            >
              ⛶
            </button>
          </>
        ) : (
          <>
            {icon ? (
              <img
                src={icon}
                alt={gameName}
                className={styles.postCardImageCls}
              />
            ) : (
              <div className={styles.postCardImagePlaceholderCls}>{gameName}</div>
            )}
            <div
              className={styles.postCardPlayOverlayCls}
              onClick={launchGame}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && launchGame()}
            >
              {isLaunching ? (
                <div className={styles.loadingSpinnerCls} />
              ) : (
                <div className={styles.postCardPlayButtonCls}>
                  <div className={styles.postCardPlayIconCls} />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Actions bar */}
      <div className={styles.postActionsBarCls}>
        <button
          type="button"
          className={styles.postActionButtonCls}
          onClick={handleLike}
          disabled={isToggling}
          aria-label={localLiked ? t("gameFeedUnlike") : t("gameFeedLike")}
        >
          <span className={`${styles.postHeartIconCls} ${localLiked ? styles.postHeartIconLikedCls : ""}`}>
            {localLiked ? "❤️" : "🤍"}
          </span>
          {localLikeCount > 0 && <span className={styles.postActionCountCls}>{localLikeCount}</span>}
        </button>

        <button
          type="button"
          className={styles.postActionButtonCls}
          onClick={() => setShowComments(true)}
          aria-label={t("gameFeedComments")}
        >
          <span className={styles.postCommentIconCls}>💬</span>
          {(game.comment_count ?? 0) > 0 && <span className={styles.postActionCountCls}>{game.comment_count}</span>}
        </button>
      </div>

      {/* Comments panel (reused from game feed) */}
      {showComments && (
        <CommentsPanel
          provider={gameProvider}
          gameId={gameId}
          onClose={() => setShowComments(false)}
        />
      )}
    </div>
  );
};

export default memo(PostCard, (prev, next) => {
  const prevId = prev.game.provider_game_id || prev.game.id || prev.game.game_id;
  const nextId = next.game.provider_game_id || next.game.id || next.game.game_id;
  return prevId === nextId && prev.providerName === next.providerName;
});
